import { useAssignKioskStore } from '../../stores/assign-kiosk-store';
import { LocationAccordionRow } from './LocationAccordionRow';

export function LocationAccordionList({ excludeLocationId }: { excludeLocationId?: string | null }) {
  const {
    locations,
    isLoadingLocations,
    locationsFetched,
    locationsError,
    locationSearchQuery,
    fetchLocations,
    kioskDetails,
  } = useAssignKioskStore();

  // ── Not fetched yet (initial state before init runs) ─────────────────────
  if (!isLoadingLocations && !locationsFetched) {
    return null;
  }

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (isLoadingLocations) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 border-b border-gray-100 px-4 py-4 last:border-b-0">
            <div className="h-5 w-9 animate-pulse rounded-full bg-gray-200" />
            <div className="flex flex-1 flex-col gap-1.5">
              <div className="h-4 w-44 animate-pulse rounded bg-gray-200" />
              <div className="h-3 w-28 animate-pulse rounded bg-gray-100" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────
  if (locationsError) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 py-10 text-center">
        <p className="text-sm text-red-700">{locationsError}</p>
        <button
          onClick={fetchLocations}
          className="rounded-md bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // ── Empty — fetched but no locations for this operator ────────────────────
  if (locations.length === 0) {
    return (
      <div className="flex min-h-[32rem] items-center justify-center rounded-lg border border-gray-200 bg-white">
        <p className="text-sm text-gray-400">No locations found for this operator.</p>
      </div>
    );
  }

  // ── Filter by search query ────────────────────────────────────────────────
  const query = locationSearchQuery.trim().toLowerCase();
  const assignedId = kioskDetails?.locationId ?? null;
  // Exclude the already-assigned location and any location shown in the header
  const unassigned = locations.filter(
    (l) => l.id !== assignedId && l.id !== excludeLocationId
  );
  const sorted = [...unassigned].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  const filtered = query
    ? sorted.filter((l) => (l.name ?? '').toLowerCase().includes(query))
    : sorted;

  // ── No search results ─────────────────────────────────────────────────────
  if (filtered.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white py-10 text-center text-sm text-gray-400">
        No locations match &ldquo;{locationSearchQuery}&rdquo;
      </div>
    );
  }

  // ── Accordion list ────────────────────────────────────────────────────────
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {filtered.map((location) => (
        <LocationAccordionRow key={location.id} location={location} />
      ))}
    </div>
  );
}
